import React, { useMemo } from 'react';
import { S15TwoSections, S15TwoSectionsProps, MilanGradientColors, MilanBackgroundColors } from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap, useImageKey } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';

type S15TwoSectionsBuilderProps = Omit<S15TwoSectionsProps, 'itemBackgroundColor'> & {
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundGradient?: MilanGradientColors | 'None' | 'default';
    waveGradient?: MilanGradientColors | 'None' | 'default';
    itemBackgroundColor?: string;
};

export function S15TwoSectionsSection(props: S15TwoSectionsBuilderProps) {
    const { replaceVariables } = useFirebaseMetrics();
    const {
        headlineText,
        headlineColor,
        text,
        textColor,
        itemBackgroundColor,
        itemHeadlineColor,
        itemTextColor,
        items,
        backgroundFlipWave,
        backgroundWaveColor,
        backgroundImage,
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        waveGradient,
        ...restProps
    } = props;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const processedItemBackgroundColor = itemBackgroundColor === 'None' ? 'White' : itemBackgroundColor;

    const processedItems = items?.map(item => ({
        ...item,
        headlineText: replaceVariables(item.headlineText),
        text: replaceVariables(item.text),
        imagePosition: item.imagePosition ? 'right' as const : 'left' as const
    }));

    const modifiedProps = {
        ...restProps,
        backgroundGradient: props.backgroundGradient === ('None' as MilanGradientColors | 'None') ? 'default' : props.backgroundGradient,
        waveGradient: waveGradient === ('None' as MilanGradientColors | 'None') ? 'default' : waveGradient,
        backgroundColor: props.backgroundColor || 'default',
        className: `mcl-mx-auto ${topPaddingClasses} ${bottomPaddingClasses}`,
    };

    const newKeyOnItemsChange = useMemo(() => useImageKey(processedItems), [processedItems]);

    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: props.backgroundColor || 'default',
        gradientColor: props.backgroundGradient || 'default',
    });

    return (
        <div className={`${backgroundColor}`}>
            <S15TwoSections
                key={newKeyOnItemsChange}
                {...modifiedProps}
                headlineText={headlineText}
                headlineColor={headlineColor}
                text={replaceVariables(text)}
                textColor={textColor}
                itemBackgroundColor={processedItemBackgroundColor as MilanBackgroundColors}
                itemHeadlineColor={itemHeadlineColor}
                itemTextColor={itemTextColor}
                items={processedItems}
                backgroundFlipWave={backgroundFlipWave}
                backgroundWaveColor={backgroundWaveColor}
                backgroundImage={backgroundImage}
            />
        </div>
    );
}
